import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtEur } from "../api";
import { useLang } from "../i18n";
import { SignalCard } from "../components/SignalCard";
import { EquityChart } from "../components/TradesTable";
import { YearlyPerformance } from "../components/YearlyPerformance";
import { SystemsComparison, GuardianEquityChart, GuardianYearly } from "../components/Guardian";
import { ArrowRight, Zap, BarChart3, FileText, Shield } from "lucide-react";

export default function Home() {
  const { lang, t } = useLang();
  const [stats, setStats] = useState(null);
  const [equity, setEquity] = useState([]);

  useEffect(() => {
    api.get("/public/performance").then((r) => setStats(r.data)).catch(() => {});
    api.get("/public/equity").then((r) => setEquity(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/10">
        <img src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24 lg:py-32">
          <p className="text-xs tracking-[0.3em] uppercase text-emerald-400 font-mono mb-4">{t("hero_kicker")}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white max-w-3xl leading-[1.05]">
            {t("hero_title")}
          </h1>
          <p className="text-base text-slate-300 max-w-xl mt-6 leading-relaxed">{t("hero_sub")}</p>
          <div className="flex flex-wrap items-center gap-4 mt-10">
            <Link to="/pricing" data-testid="hero-cta-plans"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-medium rounded px-6 py-3 transition-colors">
              {t("hero_cta")} <ArrowRight className="w-4 h-4" />
            </Link>
            {stats && (
              <div data-testid="hero-ytd" className="flex items-baseline gap-3 border border-white/15 bg-black/40 px-5 py-3">
                <span className="text-xs uppercase tracking-widest text-slate-400">{t("ytd_profit")}</span>
                <span className={`font-mono text-2xl ${stats.ytd_eur >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {fmtEur(stats.ytd_eur, lang)}
                </span>
                <span className={`font-mono text-sm ${stats.ytd_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {stats.ytd_pct > 0 ? "+" : ""}{stats.ytd_pct}%
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 space-y-8">
        <div className="grid md:grid-cols-2 gap-4">
          <div data-testid="home-alpha-card" className="border border-emerald-500/25 bg-[#111111] p-6">
            <p className="text-xs tracking-[0.25em] uppercase text-emerald-400 font-mono">{lang === "it" ? "Sistema speculativo" : "Speculative system"}</p>
            <h2 className="font-heading text-2xl font-extrabold text-white mt-2">Bund <span className="text-emerald-400">Alpha</span></h2>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
              {lang === "it"
                ? "Stop & Reverse: sempre a mercato, long o short, per catturare il massimo movimento del Bund. ~10-12 operazioni l'anno, rendimento massimo."
                : "Stop & Reverse: always in the market, long or short, to capture the Bund's full movement. ~10-12 trades per year, maximum return."}
            </p>
            <Link to="/pricing" data-testid="home-alpha-link" className="inline-flex items-center gap-1 text-sm text-emerald-400 hover:text-emerald-300 mt-4">
              {lang === "it" ? "Piani 3/6/12 mesi + trial" : "3/6/12-month plans + trial"} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div data-testid="home-guardian-card" className="border border-sky-500/25 bg-[#0B1220] p-6">
            <p className="text-xs tracking-[0.25em] uppercase text-sky-400 font-mono flex items-center gap-2"><Shield className="w-3.5 h-3.5" />{lang === "it" ? "Sistema conservativo" : "Conservative system"}</p>
            <h2 className="font-heading text-2xl font-extrabold text-white mt-2">Bund <span className="text-sky-400">Guardian</span></h2>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
              {lang === "it"
                ? "Per l'investitore prudente: in 18 anni non ha mai chiuso un anno in perdita. Poche operazioni selezionate (anche solo 2 l'anno), stop loss rigido e profitto annuale blindato."
                : "For the prudent investor: in 18 years it has never closed a losing year. Few selected trades (even just 2 per year), hard stop loss and locked-in yearly profit."}
            </p>
            <Link to="/guardian" data-testid="home-guardian-link" className="inline-flex items-center gap-1 text-sm text-sky-400 hover:text-sky-300 mt-4">
              {lang === "it" ? "Scopri Guardian — solo annuale" : "Discover Guardian — annual only"} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
        <SystemsComparison />
        <div className="grid md:grid-cols-2 gap-6">
          <EquityChart data={equity}
            title={lang === "it" ? "Equity storica dal 2022 — Alpha System" : "Historical equity since 2022 — Alpha System"} />
          <GuardianEquityChart
            title={lang === "it" ? "Equity storica dal 2009 — Guardian System" : "Historical equity since 2009 — Guardian System"} />
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2"><YearlyPerformance /></div>
          <SignalCard locked />
        </div>
        <GuardianYearly />
        <div className="grid md:grid-cols-3 gap-6 pt-8">
          {[
            { Icon: Zap, title: lang === "it" ? "Segnale serale automatico" : "Automatic evening signal", text: lang === "it" ? "Score calcolato su trend, momentum, RSI, stagionalità e valute. Pubblicazione automatica alle 22:00." : "Score computed on trend, momentum, RSI, seasonality and FX. Published automatically at 22:00." },
            { Icon: BarChart3, title: lang === "it" ? "Contabilità stop & reverse" : "Stop & reverse accounting", text: lang === "it" ? "Sempre a mercato con 1 contratto, rollover trimestrale automatico, P/L verificabile trade per trade." : "Always in the market with 1 contract, automatic quarterly rollover, verifiable trade-by-trade P/L." },
            { Icon: FileText, title: lang === "it" ? "Report automatici" : "Automatic reports", text: lang === "it" ? "Report giornalieri e settimanali generati automaticamente, storico sempre aperto e scaricabile." : "Daily and weekly reports generated automatically, history always open and downloadable." },
          ].map((f, i) => (
            <div key={i} className="border border-white/10 bg-[#111111] p-6">
              <f.Icon className="w-6 h-6 text-emerald-400 mb-4" />
              <h3 className="font-heading font-bold text-white text-lg">{f.title}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
